export const num = (value) => parseFloat(value) || 0;

const createDefaultSettlement = () => ({
  totalAmount: 0,
  totalAmountSeller: 0,
  totalAmountJmi: 0,
  totalAmountOperator: 0,
  sellerFine: 0,
  jmiFine: 0,
  operatorFine: 0,
  fineWeight: 0,
  totalWestage: 0,
  setMc: 0,
  gramMcPerGram: 0,
  specMcPerGram: 0,
  SellerSetMc: 0,
  JmiSetMc: 0,
  OperatorSetMc: 0,
  SellerGramMcPerGram: 0,
  JmiGramMcPerGram: 0,
  OperatorGramMcPerGram: 0,
  SellerSpecMcPerGram: 0,
  JmiSpecMcPerGram: 0,
  OperatorSpecMcPerGram: 0,
  TotalSet: 0,
  TotalnetWt: 0,
  TotalgrossWt: 0,
  specificationWt: 0,
  SellerWestage: 0,
  JmiWestage: 0,
  OperatorWestage: 0,
});

/**
 * `orderDetail` is written by the seller's Final Correction step and stores
 * PER-SET averages plus a quantity, e.g.
 *   { "10": { qty: 3, grossWT: 6.2, netwt: 5.1, specWT: 1.1, ... } }
 *
 * Two things were wrong here previously:
 *   1. the persisted keys are `grossWT` / `netwt` (not `grossWt` / `netWt`),
 *      so the old lookups resolved to `undefined` and produced 0;
 *   2. the values are per-set averages, so they must be multiplied by `qty`.
 */
const detailQty = (item = {}) => {
  const q = num(item?.qty ?? item?.set ?? item?.quantity);
  return q > 0 ? q : 1; // a missing quantity means a single set
};

const detailNet = (item = {}) => num(item?.netwt ?? item?.netWt);
const detailGross = (item = {}) => num(item?.grossWT ?? item?.grossWt);
const detailSpec = (item = {}) => {
  const explicit = item?.specWT ?? item?.specWt;
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    return num(explicit);
  }
  return Math.max(0, detailGross(item) - detailNet(item));
};

const calculateSellerWeights = (orderDetail, order = {}) => {
  const weights = Object.values(orderDetail || {}).reduce(
    (acc, item) => {
      const qty = detailQty(item);
      acc.TotalSet += qty;
      acc.TotalnetWt += detailNet(item) * qty;
      acc.TotalgrossWt += detailGross(item) * qty;
      acc.TotalSpecWt += detailSpec(item) * qty;
      return acc;
    },
    { TotalSet: 0, TotalnetWt: 0, TotalgrossWt: 0, TotalSpecWt: 0 },
  );

  // Safety net for orders that never went through Final Correction and so have
  // no per-size breakdown — fall back to the order-level weights.
  if (weights.TotalSet === 0) {
    const net = num(order.netWt);
    const gross = num(order.grossWt);
    const spec = num(order.specificationWt) || Math.max(0, gross - net);
    return {
      TotalSet: num(order.totalQty) || num(order.set) || 1,
      TotalnetWt: net,
      TotalgrossWt: gross,
      TotalSpecWt: spec,
      specificationWt: spec,
    };
  }

  return {
    ...weights,
    specificationWt:
      weights.TotalSpecWt > 0
        ? weights.TotalSpecWt
        : Math.max(0, weights.TotalgrossWt - weights.TotalnetWt),
  };
};

const calculateSellerWastage = (order) => {
  const hasSpecification = Boolean(order.specification);
  const SellerWestage =
    num(order.purity) +
    num(order.wastage) +
    num(order.wastageJmi) +
    (hasSpecification ? num(order.specificationMC) : 0);
  const JmiWestage =
    num(order.buyerDeskCalculation) -
    num(order.purity) +
    num(order.wastageJmi) +
    (hasSpecification
      ? num(order.specificationMC) * (num(order.specificationMakingJmi) / 100)
      : 0);
  const OperatorWestage =
    num(order.wastageOperator) +
    (hasSpecification
      ? num(order.specificationMC) * (num(order.specificationMakingOperator) / 100)
      : 0);

  // The buyer total is DERIVED from the three slices so the parties can never
  // drift apart. It used to be summed independently, which double-counted
  // `wastageJmi` and added `specificationMakingJmi/Operator` as raw amounts even
  // though the making-charge split treats them as percentages of
  // `specificationMC`.
  const totalWestage = SellerWestage + JmiWestage + OperatorWestage;

  return { totalWestage, SellerWestage, JmiWestage, OperatorWestage };
};

const calculateSellerMakingCharges = (order) => {
  const setMcBase = num(order.setMc);
  const setMcJmiPercent = num(order.setMakingJmi) / 100;
  const setMcOperatorPercent = num(order.setMakingOperator) / 100;
  const gramMcBase = num(order.netGramMc);
  const gramMcJmiPercent = num(order.gramMakingJmi) / 100;
  const gramMcOperatorPercent = num(order.gramMakingOperator) / 100;
  const specMcBase = num(order.specificationGramRate);
  const specMcJmiPercent = num(order.specificationGramRateJmi) / 100;
  const specMcOperatorPercent = num(order.specificationGramRateOperator) / 100;

  return {
    setMc: setMcBase * (1 + setMcJmiPercent + setMcOperatorPercent),
    SellerSetMc: setMcBase,
    JmiSetMc: setMcBase * setMcJmiPercent,
    OperatorSetMc: setMcBase * setMcOperatorPercent,
    gramMcPerGram: gramMcBase * (1 + gramMcJmiPercent + gramMcOperatorPercent),
    SellerGramMcPerGram: gramMcBase,
    JmiGramMcPerGram: gramMcBase * gramMcJmiPercent,
    OperatorGramMcPerGram: gramMcBase * gramMcOperatorPercent,
    specMcPerGram: specMcBase * (1 + specMcJmiPercent + specMcOperatorPercent),
    SellerSpecMcPerGram: specMcBase,
    JmiSpecMcPerGram: specMcBase * specMcJmiPercent,
    OperatorSpecMcPerGram: specMcBase * specMcOperatorPercent,
  };
};

export const calculateOrderSettlement = (order = {}) => {
  const weights = calculateSellerWeights(order.orderDetail, order);
  const wastage = calculateSellerWastage(order);
  const mc = calculateSellerMakingCharges(order);
  const { TotalnetWt, specificationWt, TotalSet } = weights;

  // `setMc` and its Seller/Jmi/Operator splits are PER-SET rates, so they must
  // be multiplied by the number of sets. Previously only a single set was ever
  // charged, which made the amount collapse on multi-set orders.
  const setMcTotal = mc.setMc * TotalSet;
  const sellerSetMcTotal = mc.SellerSetMc * TotalSet;
  const jmiSetMcTotal = mc.JmiSetMc * TotalSet;
  const operatorSetMcTotal = mc.OperatorSetMc * TotalSet;

  return {
    totalAmount:
      setMcTotal +
      mc.gramMcPerGram * TotalnetWt +
      mc.specMcPerGram * specificationWt,
    totalAmountSeller:
      sellerSetMcTotal +
      mc.SellerGramMcPerGram * TotalnetWt +
      mc.SellerSpecMcPerGram * specificationWt,
    totalAmountJmi:
      jmiSetMcTotal +
      mc.JmiGramMcPerGram * TotalnetWt +
      mc.JmiSpecMcPerGram * specificationWt,
    totalAmountOperator:
      operatorSetMcTotal +
      mc.OperatorGramMcPerGram * TotalnetWt +
      mc.OperatorSpecMcPerGram * specificationWt,
    fineWeight: (TotalnetWt * wastage.totalWestage) / 100,
    sellerFine: (TotalnetWt * wastage.SellerWestage) / 100,
    jmiFine: (TotalnetWt * wastage.JmiWestage) / 100,
    operatorFine: (TotalnetWt * wastage.OperatorWestage) / 100,
    ...wastage,
    ...mc,
    ...weights,
  };
};

/**
 * Self-check helper. `calculateOrderSettlement` is now the single source of
 * truth for gross/net/spec/set counts, so the four parties must add back up to
 * the buyer total and the three fine slices must add up to the buyer fine.
 * Any drift here means a rate/fine rule was edited inconsistently.
 */
export const reconcileOrderSettlement = (order = {}) => {
  const s = calculateOrderSettlement(order);
  const partyAmount = s.totalAmountSeller + s.totalAmountJmi + s.totalAmountOperator;
  const partyFine = s.sellerFine + s.jmiFine + s.operatorFine;

  return {
    totalAmount: s.totalAmount,
    partyAmount,
    amountDelta: Number((s.totalAmount - partyAmount).toFixed(6)),
    fineWeight: s.fineWeight,
    partyFine,
    fineDelta: Number((s.fineWeight - partyFine).toFixed(6)),
    isBalanced: Math.abs(s.totalAmount - partyAmount) < 0.01 && Math.abs(s.fineWeight - partyFine) < 0.001,
  };
};

export const getTotalAmount = (order) => calculateOrderSettlement(order).totalAmount;
export const getSellerAmount = (order) => calculateOrderSettlement(order).totalAmountSeller;
export const getJmiAmount = (order) => calculateOrderSettlement(order).totalAmountJmi;
export const getOperatorAmount = (order) => calculateOrderSettlement(order).totalAmountOperator;
export const getSellerFine = (order) => calculateOrderSettlement(order).sellerFine;
export const getJmiFine = (order) => calculateOrderSettlement(order).jmiFine;
export const getOperatorFine = (order) => calculateOrderSettlement(order).operatorFine;
export const getFineWeight = (order) => calculateOrderSettlement(order).fineWeight;
export const getTotalWestage = (order) => calculateOrderSettlement(order).totalWestage;
export const getSetMc = (order) => calculateOrderSettlement(order).setMc;
export const getGramMcPerGram = (order) => calculateOrderSettlement(order).gramMcPerGram;
export const getSpecMcPerGram = (order) => calculateOrderSettlement(order).specMcPerGram;

export const getWeights = (order) => {
  const settlement = calculateOrderSettlement(order);
  return {
    TotalSet: settlement.TotalSet,
    TotalnetWt: settlement.TotalnetWt,
    TotalgrossWt: settlement.TotalgrossWt,
    specificationWt: settlement.specificationWt,
  };
};

export const calculateTotalPayment = (orders = []) =>
  orders.reduce((total, order) => total + calculateOrderSettlement(order).totalAmount, 0);

const calculatePartySettlement = (orders = [], fineField, amountField) =>
  orders.reduce(
    (acc, order) => {
      const settlement = calculateOrderSettlement(order);
      acc.fine += settlement[fineField];
      acc.amount += settlement[amountField];
      return acc;
    },
    { fine: 0, amount: 0 },
  );

export const calculateJmiSettlement = (orders) =>
  calculatePartySettlement(orders, "jmiFine", "totalAmountJmi");

export const calculateSellerSettlement = (orders) =>
  calculatePartySettlement(orders, "sellerFine", "totalAmountSeller");

export const calculateOperatorSettlement = (orders) =>
  calculatePartySettlement(orders, "operatorFine", "totalAmountOperator");

export const getSettlementSummary = (orders = []) => {
  const total = orders.reduce(
    (acc, order) => {
      const settlement = calculateOrderSettlement(order);
      acc.amount += settlement.totalAmount;
      acc.netWt += settlement.TotalnetWt;
      acc.grossWt += settlement.TotalgrossWt;
      acc.specificationWt += settlement.specificationWt;
      return acc;
    },
    { amount: 0, netWt: 0, grossWt: 0, specificationWt: 0 },
  );

  return {
    total: total.amount,
    jmi: calculateJmiSettlement(orders),
    seller: calculateSellerSettlement(orders),
    operator: calculateOperatorSettlement(orders),
    weights: {
      totalNetWt: total.netWt,
      totalGrossWt: total.grossWt,
      specificationWt: total.specificationWt,
    },
  };
};

export const getBuyerProductDetail = (detail = {}) => ({
  grossWT: num(detail.grossWT ?? detail.grossWt),
  netwt: num(detail.netwt ?? detail.netWt),
  qty: parseInt(detail.qty ?? detail.set ?? detail.quantity, 10) || 0,
  stampCertificateQtyPerSet: parseInt(detail.stampCertificateQtyPerSet, 10) || 0,
});

const hasNonPlaneSpecification = (order = {}) => {
  const spec = String(order.specification || "").trim();
  return Boolean(spec) && spec.toLowerCase() !== "plane";
};

/** Seller Product Details: purity + wastage + specificationMC; base MC (no JMI/operator). */
export const getSellerProductRates = (order = {}) => {
  const wastagePct = num(order.wastage) + num(order.specificationMC);
  return {
    finePct: num(order.purity) + wastagePct,
    setMcPerSet: num(order.setMc),
    gramMcPerGram: num(order.netGramMc),
    specMcPerGram: num(order.specificationGramRate),
  };
};

/** Buyer Product Details: buyerDeskCalculation + full wastage; MC with JMI/operator %. */
export const getBuyerProductRates = (order = {}) => {
  const specMc = num(order.specificationMC);
  const specAdditions = hasNonPlaneSpecification(order)
    ? (specMc * num(order.specificationMakingJmi)) / 100 +
      (specMc * num(order.specificationMakingOperator)) / 100
    : 0;
  const wastagePct =
    num(order.wastage) +
    num(order.wastageOperator) +
    num(order.wastageJmi) +
    specMc +
    specAdditions;
  const setMcBase = num(order.setMc);
  const gramMcBase = num(order.netGramMc);
  const specMcBase = num(order.specificationGramRate);

  return {
    finePct: num(order.buyerDeskCalculation) + wastagePct,
    setMcPerSet:
      setMcBase *
      (1 + (num(order.setMakingJmi) + num(order.setMakingOperator)) / 100),
    gramMcPerGram:
      gramMcBase *
      (1 + (num(order.gramMakingJmi) + num(order.gramMakingOperator)) / 100),
    specMcPerGram:
      specMcBase *
      (1 +
        (num(order.specificationGramRateJmi) +
          num(order.specificationGramRateOperator)) /
          100),
  };
};

const calculateProductLine = (order = {}, detail = {}, rates) => {
  const { grossWT, netwt, qty, stampCertificateQtyPerSet } = getBuyerProductDetail(detail);
  const gross = grossWT * qty;
  const net = netwt * qty;
  const spec = (grossWT - netwt) * qty;
  const setMcTotal = rates.setMcPerSet * qty;
  const gramTotal = rates.gramMcPerGram * net;
  const specTotal = rates.specMcPerGram * spec;
  const stampCost = num(order.stampCertificateCost);
  const stampTotal = stampCost * stampCertificateQtyPerSet * qty;

  return {
    grossWT,
    netwt,
    qty,
    stampCertificateQtyPerSet,
    gross,
    net,
    spec,
    fineWeight: (net * rates.finePct) / 100,
    setMcTotal,
    gramTotal,
    specTotal,
    stampCost,
    stampTotal,
    mcPerGram: rates.gramMcPerGram,
    total: setMcTotal + gramTotal + specTotal + stampTotal,
  };
};

export const calculateBuyerProductLine = (order = {}, detail = {}) =>
  calculateProductLine(order, detail, getBuyerProductRates(order));

export const calculateSellerProductLine = (order = {}, detail = {}) =>
  calculateProductLine(order, detail, getSellerProductRates(order));

const reduceOrderTotals = (order = {}, lineFn) => {
  const lines = Object.entries(order.orderDetail || {}).map(([size, detail]) => ({
    size,
    ...lineFn(order, detail),
  }));

  return lines.reduce(
    (totals, line) => ({
      lines: totals.lines.concat(line),
      totalQty: totals.totalQty + line.qty,
      gross: totals.gross + line.gross,
      net: totals.net + line.net,
      spec: totals.spec + line.spec,
      fineWeight: totals.fineWeight + line.fineWeight,
      totalMc: totals.totalMc + line.setMcTotal + line.gramTotal + line.specTotal,
      totalStamp: totals.totalStamp + line.stampTotal,
      totalAmount: totals.totalAmount + line.total,
    }),
    {
      lines: [],
      totalQty: 0,
      gross: 0,
      net: 0,
      spec: 0,
      fineWeight: 0,
      totalMc: 0,
      totalStamp: 0,
      totalAmount: 0,
    },
  );
};

export const calculateBuyerOrderTotals = (order = {}) =>
  reduceOrderTotals(order, calculateBuyerProductLine);

export const calculateSellerOrderTotals = (order = {}) =>
  reduceOrderTotals(order, calculateSellerProductLine);

/**
 * Pure function: Calculate Buyer Pricing for an order
 */
export const calculateBuyerPricing = (order = {}, rtgsSaleRate = 0) => {
  const settlement = calculateOrderSettlement(order);
  const netWt = num(order.netWt) || settlement.TotalnetWt || 0;
  const grossWt = num(order.grossWt) || settlement.TotalgrossWt || 0;
  const specWt = num(order.specificationWt) || settlement.specificationWt || Math.max(0, grossWt - netWt);
  const rate = num(rtgsSaleRate);
  const fineWeight = (netWt * settlement.totalWestage) / 100;

  return {
    party: "Buyer",
    finePercentage: settlement.totalWestage,
    fineWeight,
    setMc: settlement.setMc,
    gramMc: settlement.gramMcPerGram * netWt,
    specMc: settlement.specMcPerGram * specWt,
    totalMc: settlement.totalAmount,
    totalAmount: settlement.totalAmount,
    fineValuation: fineWeight * rate,
  };
};

/**
 * Pure function: Calculate Seller Pricing for an order
 */
export const calculateSellerPricing = (order = {}, rtgsSaleRate = 0) => {
  const settlement = calculateOrderSettlement(order);
  const netWt = num(order.netWt) || settlement.TotalnetWt || 0;
  const grossWt = num(order.grossWt) || settlement.TotalgrossWt || 0;
  const specWt = num(order.specificationWt) || settlement.specificationWt || Math.max(0, grossWt - netWt);
  const rate = num(rtgsSaleRate);
  const fineWeight = (netWt * settlement.SellerWestage) / 100;

  return {
    party: "Seller",
    finePercentage: settlement.SellerWestage,
    fineWeight,
    setMc: settlement.SellerSetMc,
    gramMc: settlement.SellerGramMcPerGram * netWt,
    specMc: settlement.SellerSpecMcPerGram * specWt,
    totalMc: settlement.totalAmountSeller,
    totalAmount: settlement.totalAmountSeller,
    fineValuation: fineWeight * rate,
  };
};

/**
 * Pure function: Calculate Operator Pricing for an order
 */
export const calculateOperatorPricing = (order = {}, rtgsSaleRate = 0) => {
  const settlement = calculateOrderSettlement(order);
  const netWt = num(order.netWt) || settlement.TotalnetWt || 0;
  const grossWt = num(order.grossWt) || settlement.TotalgrossWt || 0;
  const specWt = num(order.specificationWt) || settlement.specificationWt || Math.max(0, grossWt - netWt);
  const rate = num(rtgsSaleRate);
  const fineWeight = (netWt * settlement.OperatorWestage) / 100;

  return {
    party: "Operator",
    finePercentage: settlement.OperatorWestage,
    fineWeight,
    setMc: settlement.OperatorSetMc,
    gramMc: settlement.OperatorGramMcPerGram * netWt,
    specMc: settlement.OperatorSpecMcPerGram * specWt,
    totalMc: settlement.totalAmountOperator,
    totalAmount: settlement.totalAmountOperator,
    fineValuation: fineWeight * rate,
  };
};

/**
 * Pure function: Calculate JMI Pricing for an order
 */
export const calculateJmiPricing = (order = {}, rtgsSaleRate = 0) => {
  const settlement = calculateOrderSettlement(order);
  const netWt = num(order.netWt) || settlement.TotalnetWt || 0;
  const grossWt = num(order.grossWt) || settlement.TotalgrossWt || 0;
  const specWt = num(order.specificationWt) || settlement.specificationWt || Math.max(0, grossWt - netWt);
  const rate = num(rtgsSaleRate);
  const fineWeight = (netWt * settlement.JmiWestage) / 100;
  const fineAmount = fineWeight * rate;
  const totalMc = settlement.totalAmountJmi;

  return {
    party: "JMI",
    finePercentage: settlement.JmiWestage,
    fineWeight,
    setMc: settlement.JmiSetMc,
    gramMc: settlement.JmiGramMcPerGram * netWt,
    specMc: settlement.JmiSpecMcPerGram * specWt,
    totalMc,
    totalAmount: totalMc,
    fineAmount,
    totalCommission: fineAmount + totalMc,
  };
};

/**
 * Pure function: Calculate All 4-Party Pricing (Buyer, Seller, Operator, JMI) for an order
 */
export const calculateFourPartyPricing = (order = {}, rtgsSaleRate = 0) => {
  const settlement = calculateOrderSettlement(order);
  const netWt = num(order.netWt) || settlement.TotalnetWt || 0;
  const grossWt = num(order.grossWt) || settlement.TotalgrossWt || 0;
  const specWt = num(order.specificationWt) || settlement.specificationWt || Math.max(0, grossWt - netWt);

  return {
    weights: { netWt, grossWt, specWt },
    rtgsSaleRate: num(rtgsSaleRate),
    buyer: calculateBuyerPricing(order, rtgsSaleRate),
    seller: calculateSellerPricing(order, rtgsSaleRate),
    operator: calculateOperatorPricing(order, rtgsSaleRate),
    jmi: calculateJmiPricing(order, rtgsSaleRate),
  };
};

export const getDefaultSettlement = createDefaultSettlement;

